/**
 * Expense Report Detail View
 *
 * Displays a single expense report with:
 * - Report header (linked request info, status, dates)
 * - Budget tracking progress bar
 * - Payment profile details (for Team Assistants)
 * - Receipts list with thumbnails and modal viewer
 * - Add receipt functionality
 * - Action buttons for Team Assistants (EPO, PDF, Payment)
 */

import { renderIcon } from '../utils/icons';
import { createSidebarLayout } from '../components/sidebar-layout';
import { createExpenseStatusBadge } from '../components/expense-status-badge';
import { createBudgetProgress } from '../components/budget-progress';
import { createBudgetSummary } from '../components/budget-summary';
import { createReceiptList } from '../components/receipt-list';
import { createStatusHistory } from '../components/status-history';
import { createPaymentStatusActions } from '../components/payment-status-actions';
import { showToast } from '../components/toast';
import { confirm, prompt } from '../components/modal';
import { createSubmissionDialog, type BudgetSummary } from '../components/submission-dialog';
import { validatePaymentProfileForSubmission } from '../components/payment-profile-check';
import { createValidationErrorModal, parseValidationErrors } from '../components/validation-error-display';
import { createCameraCapture } from '../components/camera-capture';
import { showReceiptProcessingModal } from '../components/receipt-processing-modal';
import {
  subscribeToAuth,
  selectUser,
  type AuthState,
} from '../services/auth-store';
import { expenseReportsStore, type ExpenseReportsState } from '../services/expense-reports-store';
import { authStore } from '../services/auth-store';
import { downloadEpoFile } from '../services/epo-service';
import type { RouteContext } from '../services/router';
import { apiClient } from '../services/api-client';
import type { ExpenseReportDetail as ExpenseReportDetailType, Receipt } from '../types/expense-tracker';
import { formatCurrency } from '../types/expense-tracker';

/**
 * Format date for display
 */
function formatDate(dateString: string | null): string {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-CH', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return dateString;
  }
}

/**
 * Format IBAN with masking for security
 */
function maskIban(iban: string | null): string {
  if (!iban) return 'Not provided';

  // Already formatted with spaces - just return it
  if (iban.includes(' ')) return iban;

  const cleaned = iban.replace(/\s/g, '');
  if (cleaned.length < 8) return iban;

  const prefix = cleaned.slice(0, 4);
  const suffix = cleaned.slice(-4);
  const middle = '*'.repeat(cleaned.length - 8);

  // Format with spaces every 4 characters
  const masked = `${prefix}${middle}${suffix}`;
  return masked.replace(/(.{4})/g, '$1 ').trim();
}

/**
 * Format address from payment profile
 */
function formatAddress(profile: { street_address: string | null; postal_code: string | null; city: string | null; country: string | null } | null): string {
  if (!profile) return 'Not available';
  return `${profile.street_address}, ${profile.postal_code} ${profile.city}, ${profile.country}`;
}

/**
 * Navigate to a path
 */
function navigateTo(path: string): void {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/**
 * Create receipt upload options modal
 */
function showUploadOptions(): Promise<'camera' | 'gallery' | null> {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4';

    const modal = document.createElement('div');
    modal.className = 'glass-card rounded-2xl p-6 max-w-sm w-full';

    // Check if camera is available
    const hasCamera = 'mediaDevices' in navigator && 'getUserMedia' in navigator.mediaDevices;

    modal.innerHTML = `
      <div class="text-center mb-6">
        <h3 class="text-lg font-semibold text-foreground">Add Receipt</h3>
        <p class="text-sm text-muted-foreground">Choose how to add your receipt</p>
      </div>
      <div class="space-y-3">
        ${hasCamera ? `
          <button id="camera-btn" class="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-border hover:bg-muted/50 transition-colors text-left">
            ${renderIcon('camera', { size: 'md', className: 'text-primary' }).outerHTML}
            <div>
              <p class="font-medium text-foreground">Capture from Camera</p>
              <p class="text-xs text-muted-foreground">Take a photo of your receipt</p>
            </div>
          </button>
        ` : ''}
        <button id="gallery-btn" class="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-border hover:bg-muted/50 transition-colors text-left">
          ${renderIcon('image', { size: 'md', className: 'text-primary' }).outerHTML}
          <div>
            <p class="font-medium text-foreground">Upload from Gallery</p>
            <p class="text-xs text-muted-foreground">Select an existing image</p>
          </div>
        </button>
        <button id="cancel-btn" class="w-full px-4 py-2 text-muted-foreground hover:text-foreground transition-colors">
          Cancel
        </button>
      </div>
    `;

    const cleanup = () => overlay.remove();

    modal.querySelector('#camera-btn')?.addEventListener('click', () => {
      cleanup();
      resolve('camera');
    });

    modal.querySelector('#gallery-btn')?.addEventListener('click', () => {
      cleanup();
      resolve('gallery');
    });

    modal.querySelector('#cancel-btn')?.addEventListener('click', () => {
      cleanup();
      resolve(null);
    });

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        cleanup();
        resolve(null);
      }
    });

    overlay.appendChild(modal);
    document.body.appendChild(overlay);
  });
}

/**
 * Handle file selection from gallery
 */
function selectFileFromGallery(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.style.display = 'none';

    input.addEventListener('change', () => {
      const file = input.files?.[0] ?? null;
      input.remove();
      resolve(file);
    });

    input.addEventListener('cancel', () => {
      input.remove();
      resolve(null);
    });

    document.body.appendChild(input);
    input.click();
  });
}

/**
 * Capture photo from camera using camera capture modal
 */
function captureFromCamera(): Promise<File | null> {
  return new Promise((resolve) => {
    const camera = createCameraCapture({
      onCapture: (file) => {
        resolve(file);
      },
      onCancel: () => {
        resolve(null);
      },
    });

    camera.open();
  });
}

/**
 * Create warning banner for unapproved expense request
 */
function createUnapprovedRequestWarning(): HTMLElement {
  const warning = document.createElement('div');
  warning.className = 'glass-card rounded-2xl p-4 mb-6 border-2 border-warning/50';
  warning.setAttribute('data-testid', 'unapproved-request-warning');

  warning.innerHTML = `
    <div class="flex items-start gap-3">
      <div class="flex-shrink-0 mt-0.5">
        ${renderIcon('alertTriangle', { size: 'md', className: 'text-warning' }).outerHTML}
      </div>
      <div class="flex-1">
        <p class="font-medium text-foreground mb-1">Linked Request Not Approved</p>
        <p class="text-sm text-muted-foreground">
          Note: Receipts can be uploaded, but this Expense Report cannot be submitted until the linked Expense Request has been approved.
        </p>
      </div>
    </div>
  `;

  return warning;
}

/**
 * Create report header section
 */
function createReportHeader(
  report: ExpenseReportDetailType,
  onSubmit?: () => Promise<void>
): HTMLElement {
  const header = document.createElement('div');
  header.className = 'glass-card rounded-2xl p-6 mb-6';
  header.setAttribute('data-testid', 'report-header');

  const request = report.expenditure_request;
  const isEditable = report.isEditable ?? report.status === 'DRAFT';

  header.innerHTML = `
    <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
      <div class="flex-1">
        <div class="flex items-center gap-3 mb-2">
          <h2 class="text-xl font-semibold text-foreground">${request?.title || 'Expense Report'}</h2>
          <div id="status-badge"></div>
        </div>

        ${request ? `
          <div class="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground mb-4">
            <span class="flex items-center gap-1">
              ${renderIcon('wallet', { size: 'sm' }).outerHTML}
              <span>Approved: ${formatCurrency(request.amount, request.currency)}</span>
            </span>
            <span class="flex items-center gap-1">
              ${renderIcon('calendar', { size: 'sm' }).outerHTML}
              <span>Created: ${formatDate(report.created_at)}</span>
            </span>
            ${report.submitted_at ? `
              <span class="flex items-center gap-1">
                ${renderIcon('send', { size: 'sm' }).outerHTML}
                <span>Submitted: ${formatDate(report.submitted_at)}</span>
              </span>
            ` : ''}
          </div>
        ` : ''}

        <div class="flex items-center gap-3">
          <a href="/requests/${report.expenditure_request_id}" id="view-request-link" class="inline-flex items-center gap-1 text-sm text-primary hover:underline">
            ${renderIcon('externalLink', { size: 'xs' }).outerHTML}
            <span>View Original Request</span>
          </a>

          ${isEditable && onSubmit ? `
            <button id="submit-btn" class="inline-flex items-center gap-1 px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-md transition-all">
              ${renderIcon('send', { size: 'sm' }).outerHTML}
              <span>Submit for Reimbursement</span>
            </button>
          ` : ''}

          ${report.digital_signature_confirmed ? `
            <span class="inline-flex items-center gap-1 text-sm text-green-500">
              ${renderIcon('checkCircle', { size: 'sm' }).outerHTML}
              <span>Digitally Signed</span>
            </span>
          ` : ''}
        </div>
      </div>

      <div class="lg:w-64" id="budget-progress"></div>
    </div>
  `;

  // Add status badge
  const badgeContainer = header.querySelector('#status-badge');
  if (badgeContainer) {
    const badge = createExpenseStatusBadge(report.status);
    badge.setAttribute('data-testid', 'status-badge');
    badgeContainer.appendChild(badge);
  }

  // Add budget summary if available, otherwise fallback to simple progress
  const progressContainer = header.querySelector('#budget-progress');
  if (progressContainer && request) {
    // Check if report has budgetSummary from the API
    let budgetElement: HTMLElement;
    if ((report as any).budgetSummary) {
      budgetElement = createBudgetSummary((report as any).budgetSummary);
    } else {
      // Fallback to simple progress bar
      budgetElement = createBudgetProgress({
        approvedAmount: request.amount,
        spentAmount: report.total_amount_chf ?? 0,
        currency: request.currency,
      });
    }
    budgetElement.setAttribute('data-testid', 'budget-progress');
    progressContainer.appendChild(budgetElement);
  }

  // Handle link click
  const link = header.querySelector('#view-request-link');
  if (link) {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      navigateTo(`/requests/${report.expenditure_request_id}`);
    });
  }

  // Handle submit button click
  const submitBtn = header.querySelector('#submit-btn');
  if (submitBtn && onSubmit) {
    submitBtn.addEventListener('click', onSubmit);
  }

  return header;
}

/**
 * Create payment profile section (for Team Assistants to verify payment details)
 */
function createPaymentProfileSection(report: ExpenseReportDetailType): HTMLElement {
  const section = document.createElement('div');
  section.className = 'glass-card rounded-2xl p-6 mb-6';
  section.setAttribute('data-testid', 'payment-profile');

  const profile = report.payment_profile;
  const hasProfile = profile && profile.iban;

  section.innerHTML = `
    <h3 class="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
      ${renderIcon('creditCard', { size: 'md' }).outerHTML}
      Payment Details
    </h3>
    ${hasProfile ? `
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p class="text-sm text-muted-foreground mb-1">Account Holder</p>
          <p class="font-medium text-foreground">${profile.account_holder_name || '—'}</p>
        </div>
        <div>
          <p class="text-sm text-muted-foreground mb-1">IBAN</p>
          <p class="font-mono text-foreground" data-testid="iban">${maskIban(profile.iban)}</p>
        </div>
        ${profile.swift_bic ? `
          <div>
            <p class="text-sm text-muted-foreground mb-1">SWIFT/BIC</p>
            <p class="font-mono text-foreground">${profile.swift_bic}</p>
          </div>
        ` : ''}
        ${profile.street_address ? `
          <div>
            <p class="text-sm text-muted-foreground mb-1">Address</p>
            <p class="text-foreground">${formatAddress(profile)}</p>
          </div>
        ` : ''}
      </div>
    ` : `
      <div class="flex items-center gap-3 p-4 rounded-lg bg-warning/10 border border-warning/20" data-testid="payment-profile-warning">
        ${renderIcon('alertTriangle', { size: 'md', className: 'text-warning' }).outerHTML}
        <div>
          <p class="font-medium text-foreground">Payment profile not set up</p>
          <p class="text-sm text-muted-foreground">The submitter hasn't set up their payment profile yet.</p>
        </div>
      </div>
    `}
  `;

  return section;
}

/**
 * Create linked request details section (FR34)
 */
function createLinkedRequestSection(report: ExpenseReportDetailType): HTMLElement {
  const section = document.createElement('div');
  section.className = 'glass-card rounded-2xl p-6 mb-6';
  section.setAttribute('data-testid', 'linked-request');

  const linkedRequest = report.linked_request || report.expenditure_request;
  const budget = report.budget;

  section.innerHTML = `
    <h3 class="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
      ${renderIcon('fileText', { size: 'md' }).outerHTML}
      Linked Expenditure Request
    </h3>
    ${linkedRequest ? `
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <p class="text-sm text-muted-foreground mb-1">Request Title</p>
          <p class="font-medium text-foreground">${linkedRequest.title}</p>
        </div>
        <div>
          <p class="text-sm text-muted-foreground mb-1">Approved Amount</p>
          <p class="font-medium text-foreground">${formatCurrency(linkedRequest.approved_amount || linkedRequest.amount, linkedRequest.currency)}</p>
        </div>
        ${linkedRequest.requester_name || report.submitter ? `
          <div>
            <p class="text-sm text-muted-foreground mb-1">Requester</p>
            <p class="text-foreground">${linkedRequest.requester_name || report.submitter?.display_name || '—'}</p>
          </div>
        ` : ''}
        ${linkedRequest.approval_date || linkedRequest.approved_at ? `
          <div>
            <p class="text-sm text-muted-foreground mb-1">Approval Date</p>
            <p class="text-foreground">${formatDate(linkedRequest.approval_date || linkedRequest.approved_at)}</p>
          </div>
        ` : ''}
      </div>
      ${budget ? `
        <div class="mt-4 p-4 rounded-lg bg-muted/30">
          <div class="flex justify-between items-center mb-2">
            <span class="text-sm text-muted-foreground">Budget Usage</span>
            <span class="text-sm font-medium ${budget.percentUsed > 100 ? 'text-destructive' : 'text-foreground'}">${budget.percentUsed.toFixed(1)}%</span>
          </div>
          <div class="w-full h-2 bg-muted rounded-full overflow-hidden mb-2">
            <div class="h-full ${budget.percentUsed > 100 ? 'bg-destructive' : 'bg-primary'} transition-all" style="width: ${Math.min(budget.percentUsed, 100)}%"></div>
          </div>
          <div class="flex justify-between text-sm">
            <span class="text-muted-foreground">Spent: ${formatCurrency(budget.spent, budget.currency)}</span>
            <span class="text-muted-foreground">Remaining: ${formatCurrency(budget.remaining, budget.currency)}</span>
          </div>
        </div>
      ` : ''}
      <a href="/requests/${report.expenditure_request_id}" class="inline-flex items-center gap-1 text-sm text-primary hover:underline mt-4 view-request-link">
        ${renderIcon('externalLink', { size: 'xs' }).outerHTML}
        <span>View Original Request</span>
      </a>
    ` : `
      <p class="text-muted-foreground">No linked expenditure request found.</p>
    `}
  `;

  // Handle link click
  const link = section.querySelector('.view-request-link');
  if (link) {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      navigateTo(`/requests/${report.expenditure_request_id}`);
    });
  }

  return section;
}

/**
 * Create action buttons section for Team Assistants
 */
function createActionButtonsSection(
  report: ExpenseReportDetailType,
  onGenerateEPO: () => void,
  onExportPDF: () => void,
  onSyncQuickBooks: () => void
): HTMLElement {
  const section = document.createElement('div');
  section.className = 'glass-card rounded-2xl p-6 mb-6';

  const canGenerateEPO = report.status === 'SUBMITTED';
  const canSyncQB = (report.status === 'SUBMITTED' || report.status === 'PAID') && !report.quickbooks_bill_id;
  const alreadySynced = !!(report.quickbooks_bill_id);

  section.innerHTML = `
    <h3 class="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
      ${renderIcon('settings', { size: 'md' }).outerHTML}
      Actions
    </h3>
    <div class="flex flex-wrap gap-3">
      <button id="epo-btn" class="inline-flex items-center gap-2 px-4 py-2 font-medium rounded-lg transition-colors ${canGenerateEPO ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-muted text-muted-foreground cursor-not-allowed'}" ${!canGenerateEPO ? 'disabled' : ''}>
        ${renderIcon('fileText', { size: 'sm' }).outerHTML}
        <span>Generate EPO File</span>
      </button>
      ${report.status !== 'DRAFT' ? `
        <button id="pdf-btn" class="inline-flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground font-medium rounded-lg hover:bg-secondary/90 transition-colors">
          ${renderIcon('download', { size: 'sm' }).outerHTML}
          <span>Export PDF</span>
        </button>
      ` : ''}
      ${(report.status === 'SUBMITTED' || report.status === 'PAID') ? `
        <button id="qb-sync-btn" data-testid="qb-sync-btn"
          class="inline-flex items-center gap-2 px-4 py-2 font-medium rounded-lg transition-colors ${canSyncQB ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-muted text-muted-foreground cursor-not-allowed'}"
          ${!canSyncQB ? 'disabled' : ''}>
          ${renderIcon('upload', { size: 'sm' }).outerHTML}
          <span id="qb-sync-btn-label">${alreadySynced ? 'Synced to QuickBooks' : 'Book in QuickBooks'}</span>
        </button>
      ` : ''}
    </div>
    ${report.status === 'PAID' ? `
      <p class="text-sm text-success mt-3">
        ${renderIcon('checkCircle', { size: 'xs', className: 'inline mr-1' }).outerHTML}
        This expense report has been paid.
      </p>
    ` : ''}
    ${alreadySynced ? `
      <p class="text-sm text-success mt-3" data-testid="qb-synced-info">
        ${renderIcon('checkCircle', { size: 'xs', className: 'inline mr-1' }).outerHTML}
        Synced to QuickBooks (Bill #${report.quickbooks_bill_id})
        ${report.quickbooks_synced_at ? ` on ${new Date(report.quickbooks_synced_at).toLocaleDateString('de-CH')}` : ''}
      </p>
    ` : ''}
    ${report.quickbooks_sync_error && !alreadySynced ? `
      <p class="text-sm text-destructive mt-3" data-testid="qb-sync-error">
        ${renderIcon('alertCircle', { size: 'xs', className: 'inline mr-1' }).outerHTML}
        QuickBooks sync error: <span id="qb-sync-error-text"></span>
      </p>
    ` : ''}
  `;

  // Add event listeners
  const epoBtn = section.querySelector('#epo-btn');
  if (epoBtn && canGenerateEPO) {
    epoBtn.addEventListener('click', onGenerateEPO);
  }

  // Only add PDF button listener if button exists (not shown for DRAFT status)
  const pdfBtn = section.querySelector('#pdf-btn');
  if (pdfBtn) {
    pdfBtn.addEventListener('click', onExportPDF);
  }

  // QuickBooks sync button
  const qbBtn = section.querySelector('#qb-sync-btn') as HTMLButtonElement | null;
  if (qbBtn && canSyncQB) {
    qbBtn.addEventListener('click', onSyncQuickBooks);
  }

  // Set sync error text safely via textContent to prevent XSS
  const syncErrorText = section.querySelector('#qb-sync-error-text');
  if (syncErrorText && report.quickbooks_sync_error) {
    syncErrorText.textContent = report.quickbooks_sync_error;
  }

  return section;
}

/**
 * Create receipts section
 */
function createReceiptsSection(
  report: ExpenseReportDetailType,
  onAddReceipt: () => void,
  onReceiptClick: (receiptId: string) => void,
  onDeleteReceipt: (receiptId: string) => void,
  onDownloadReceipt: (receiptId: string) => void
): HTMLElement {
  const section = document.createElement('div');
  section.className = 'glass-card rounded-2xl p-6';
  section.setAttribute('data-testid', 'receipts-section');

  // Section header
  const sectionHeader = document.createElement('div');
  sectionHeader.className = 'flex items-center justify-between mb-6';

  const titleWrapper = document.createElement('div');
  titleWrapper.innerHTML = `
    <h3 class="text-lg font-semibold text-foreground">Receipts</h3>
    <p class="text-sm text-muted-foreground">${report.receipts.length} receipt${report.receipts.length !== 1 ? 's' : ''} • Total: ${formatCurrency(report.total_amount_chf, 'CHF')}</p>
  `;
  sectionHeader.appendChild(titleWrapper);

  // Action buttons container
  const actionsContainer = document.createElement('div');
  actionsContainer.className = 'flex gap-2';

  // Add receipt button (only for drafts)
  if (report.status === 'DRAFT') {
    const addBtn = document.createElement('button');
    addBtn.className = 'inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors';
    addBtn.innerHTML = `
      ${renderIcon('plus', { size: 'sm' }).outerHTML}
      <span>Add Receipt</span>
    `;
    addBtn.addEventListener('click', onAddReceipt);
    actionsContainer.appendChild(addBtn);
  }

  if (actionsContainer.children.length > 0) {
    sectionHeader.appendChild(actionsContainer);
  }

  section.appendChild(sectionHeader);

  // Receipts list
  const receiptListOptions = {
    receipts: report.receipts,
    onReceiptClick,
    onDownloadReceipt,
    isEditable: report.status === 'DRAFT',
    ...(report.status === 'DRAFT' && { onAddReceipt }),
    ...(report.status === 'DRAFT' && { onDeleteReceipt }),
  };
  const receiptsList = createReceiptList(receiptListOptions);
  section.appendChild(receiptsList);

  return section;
}

/**
 * Create loading state
 */
function createLoadingState(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'text-center py-16';
  container.innerHTML = `
    <div class="animate-spin w-8 h-8 mx-auto mb-4 text-primary">
      ${renderIcon('loader', { size: 'lg' }).outerHTML}
    </div>
    <p class="text-muted-foreground">Loading expense report...</p>
  `;
  return container;
}

/**
 * Create error state
 */
function createErrorState(error: string, onBack: () => void): HTMLElement {
  const container = document.createElement('div');
  container.className = 'text-center py-16';

  const isAccessDenied = /permission|forbidden|403|401|authorized/i.test(error);
  const title = isAccessDenied ? 'Access Denied' : 'Failed to load expense report';
  const displayMessage = isAccessDenied
    ? 'You do not have permission to view this expense report.'
    : error;

  container.innerHTML = `
    <div class="w-16 h-16 mx-auto mb-4 rounded-full bg-destructive/10 flex items-center justify-center">
      ${renderIcon('alertCircle', { size: 'xl', className: 'text-destructive' }).outerHTML}
    </div>
    <h3 class="text-lg font-semibold text-foreground mb-2">${title}</h3>
    <p class="text-muted-foreground mb-4">${displayMessage}</p>
  `;

  const backBtn = document.createElement('button');
  backBtn.className = 'inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors';
  backBtn.innerHTML = `
    ${renderIcon('arrowLeft', { size: 'sm' }).outerHTML}
    <span>Back to Reports</span>
  `;
  backBtn.addEventListener('click', onBack);
  container.appendChild(backBtn);

  return container;
}

/**
 * Create not found state
 */
function createNotFoundState(onBack: () => void): HTMLElement {
  const container = document.createElement('div');
  container.className = 'text-center py-16';

  container.innerHTML = `
    <div class="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
      ${renderIcon('file', { size: 'xl', className: 'text-muted-foreground' }).outerHTML}
    </div>
    <h3 class="text-lg font-semibold text-foreground mb-2">Expense report not found</h3>
    <p class="text-muted-foreground mb-4">The expense report you're looking for doesn't exist or has been deleted.</p>
  `;

  const backBtn = document.createElement('button');
  backBtn.className = 'inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors';
  backBtn.innerHTML = `
    ${renderIcon('arrowLeft', { size: 'sm' }).outerHTML}
    <span>Back to Reports</span>
  `;
  backBtn.addEventListener('click', onBack);
  container.appendChild(backBtn);

  return container;
}

/**
 * Create the expense report detail view
 */
export function expenseReportDetailView(context: RouteContext): HTMLElement {
  const reportId = context.params.id!;

  const content = document.createElement('div');
  content.className = 'px-4 lg:px-6 py-6';

  // Back button
  const backBtn = document.createElement('button');
  backBtn.className = 'inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors';
  backBtn.innerHTML = `
    ${renderIcon('arrowLeft', { size: 'sm' }).outerHTML}
    <span>Back to Expense Reports</span>
  `;
  backBtn.addEventListener('click', () => navigateTo('/expense-reports'));
  content.appendChild(backBtn);

  // Content container
  const contentContainer = document.createElement('div');
  contentContainer.id = 'report-content';
  content.appendChild(contentContainer);

  // Store subscription
  let unsubscribe: (() => void) | null = null;
  let authUnsubscribe: (() => void) | null = null;
  let isUploadingReceipt = false;
  let currentUser: ReturnType<typeof selectUser> = null;

  // Subscribe to auth state to get user role
  authUnsubscribe = subscribeToAuth((state: AuthState) => {
    currentUser = selectUser(state);
  });

  /**
   * Check if current user is Team Assistant or Admin
   */
  function isTeamAssistantOrAdmin(): boolean {
    return currentUser?.role === 'ASSISTANT' || currentUser?.role === 'ADMIN';
  }

  /**
   * Handle Generate EPO action
   */
  async function handleGenerateEPO(): Promise<void> {
    const state = expenseReportsStore.getState();
    const report = state.selectedReport;

    if (!report) return;

    try {
      await downloadEpoFile(report.id);
    } catch (error) {
      // Error is already handled by downloadEpoFile
    }
  }

  /**
   * Handle Export PDF action
   */
  async function handleExportPDF(): Promise<void> {
    const state = expenseReportsStore.getState();
    const report = state.selectedReport;

    if (!report) return;

    showToast({ type: 'info', message: 'Generating PDF...' });

    try {
      // Generate filename - use reference if available, otherwise derive from ID
      const dateStr = new Date().toISOString().split('T')[0];
      const reference = report.reference || `EXP-${report.id.substring(0, 8).toUpperCase()}`;
      const filename = `expense-report-${reference}-${dateStr}.pdf`;

      // Download PDF via native fetch to handle blob response
      const authState = authStore.getState();
      const token = authState.accessToken;

      const response = await fetch(`/api/expense-reports/${report.id}/pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to export PDF' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showToast({ type: 'success', message: 'PDF exported successfully' });
    } catch (error: any) {
      console.error('PDF export failed:', error);
      showToast({
        type: 'error',
        message: error.message || 'Failed to export PDF',
      });
    }
  }

  /**
   * Handle add receipt action
   */
  async function handleAddReceipt(): Promise<void> {
    if (isUploadingReceipt) return;

    const choice = await showUploadOptions();
    if (!choice) return;

    let file: File | null = null;

    if (choice === 'camera') {
      // Open camera capture modal
      file = await captureFromCamera();
    } else {
      file = await selectFileFromGallery();
    }

    if (!file) return;

    isUploadingReceipt = true;

    // Show processing modal with retry support
    let capturedFile = file; // Capture file for retry
    const processingModal = showReceiptProcessingModal({
      onRetry: async () => {
        // Retry the upload with the same file
        if (capturedFile) {
          isUploadingReceipt = false;
          // Re-initiate upload by simulating the same flow
          await performUpload(capturedFile);
        }
      },
    });

    await performUpload(file);

    async function performUpload(fileToUpload: File): Promise<void> {
      try {
        // Stage 1: Uploading the receipt image (modal starts in this stage)
        const formData = new FormData();
        formData.append('receipt', fileToUpload);

        const response = await apiClient.post<{ data: Receipt }>(
          `/expense-reports/${reportId}/receipts`,
          formData
        );

        const receipt = response.data?.data;

        if (!receipt) {
          throw new Error('Receipt upload failed - no receipt returned');
        }

        // Stage 2: Analyzing with AI
        processingModal.setAnalyzing();

        try {
          await apiClient.post(`/receipts/${receipt.id}/extract`);
        } catch (extractError) {
          // Extraction failure is non-fatal - user can enter data manually
          console.warn('AI extraction failed, manual entry required:', extractError);
        }

        // Refresh the report to get updated receipt data
        await expenseReportsStore.fetchReport(reportId);

        // Stage 3: Success - show briefly then auto-close
        await processingModal.setSuccess();
      } catch (error) {
        // Show error state with retry option
        processingModal.setError(
          error instanceof Error ? error.message : 'Failed to upload receipt'
        );
      } finally {
        isUploadingReceipt = false;
      }
    }
  }

  /**
   * Handle receipt click - navigate to receipt edit modal/view
   */
  function handleReceiptClick(receiptId: string): void {
    // Navigate to receipt edit view (Story 2.5 implements receipt form)
    navigateTo(`/expense-reports/${reportId}/receipts/${receiptId}`);
  }

  /**
   * Handle delete receipt action
   */
  async function handleDeleteReceipt(receiptId: string): Promise<void> {
    const confirmed = await confirm({
      title: 'Delete Receipt',
      message: 'Are you sure you want to delete this receipt? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      destructive: true,
    });

    if (!confirmed) return;

    showToast({ type: 'info', message: 'Deleting receipt...' });

    try {
      await expenseReportsStore.deleteReceipt(receiptId);
      showToast({ type: 'success', message: 'Receipt deleted successfully' });
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to delete receipt',
      });
    }
  }

  /**
   * Handle download receipt action
   */
  async function handleDownloadReceipt(receiptId: string): Promise<void> {
    try {
      // Get the receipt to determine the filename
      const state = expenseReportsStore.getState();
      const receipt = state.selectedReport?.receipts.find(r => r.id === receiptId);

      // Create a filename based on vendor and date
      let filename = `receipt-${receiptId.slice(0, 8)}.jpg`;
      if (receipt) {
        const vendor = receipt.vendor || receipt.vendor_name || 'receipt';
        const date = receipt.receipt_date ? new Date(receipt.receipt_date).toISOString().split('T')[0] : '';
        const sanitizedVendor = vendor.replace(/[^a-z0-9]/gi, '-').toLowerCase();
        filename = date ? `${date}-${sanitizedVendor}.jpg` : `${sanitizedVendor}.jpg`;
      }

      // Get the presigned URL or download directly based on storage type
      // Use format=json to get the URL instead of following the redirect
      const response = await apiClient.get<{ url?: string; mimeType?: string }>(`/receipts/${receiptId}/image?format=json`);

      // If we get a presigned URL (S3), open it directly in a new tab
      // This avoids CORS issues that occur with fetch()
      if (response.data && response.data.url) {
        // Open the presigned URL directly - browser will download/display it
        window.open(response.data.url, '_blank');
      } else {
        // For local storage, fetch with auth header
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '/api';
        const token = localStorage.getItem('accessToken');
        const downloadUrl = `${apiBaseUrl}/receipts/${receiptId}/image`;

        const fetchResponse = await fetch(downloadUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!fetchResponse.ok) {
          throw new Error('Failed to download receipt image');
        }

        const blob = await fetchResponse.blob();
        const objectUrl = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = filename;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();

        // Cleanup
        setTimeout(() => {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(objectUrl);
        }, 100);
      }

      showToast({ type: 'success', message: 'Receipt downloaded successfully' });
    } catch (error) {
      console.error('Failed to download receipt:', error);
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to download receipt',
      });
    }
  }

  /**
   * Handle report submission
   */
  async function handleSubmitReport(): Promise<void> {
    const state = expenseReportsStore.getState();
    const report = state.selectedReport;

    if (!report) return;

    // Check payment profile first
    const hasProfile = await validatePaymentProfileForSubmission();
    if (!hasProfile) return;

    // Calculate total from receipts (sum of individual receipt amounts)
    const totalFromReceipts = (report.receipts || []).reduce(
      (sum, receipt) => sum + (receipt.amount_chf ?? 0),
      0
    );

    // Calculate budget summary
    const budgetSummary: BudgetSummary = {
      totalSpent: totalFromReceipts,
      approvedBudget: report.expenditure_request?.amount ?? 0,
      underBudget: totalFromReceipts <= (report.expenditure_request?.amount ?? 0),
      overBudgetAmount: Math.max(0, totalFromReceipts - (report.expenditure_request?.amount ?? 0))
    };

    // Show submission dialog - build props conditionally to satisfy exactOptionalPropertyTypes
    const dialogProps: Parameters<typeof createSubmissionDialog>[0] = {
      report: report as any, // Type conversion needed
      receipts: (report.receipts || []) as any, // Type conversion needed
      budgetSummary,
      onSubmit: async () => {
        try {
          // Submit the report
          await apiClient.post(`/expense-reports/${reportId}/submit`, {
            digitalSignature: true,
            signatureText: 'I confirm these expenses are accurate and business-related'
          });

          showToast({ type: 'success', message: 'Expense report submitted successfully!' });

          // Refresh the report data
          await expenseReportsStore.fetchReport(reportId);
        } catch (error: any) {
          console.error('Submission failed:', error);

          // Handle validation errors
          if (error.response?.data?.validationErrors) {
            const errors = parseValidationErrors(error.response.data);
            const errorModal = createValidationErrorModal({
              errors,
              onFix: (err) => {
                // Navigate to appropriate place to fix the error
                if (err.field?.includes('receipt')) {
                  const receiptId = err.field.split('.')[1];
                  navigateTo(`/expense-reports/${reportId}/receipts/${receiptId}`);
                } else if (err.code === 'NO_PAYMENT_PROFILE') {
                  navigateTo('/payment-profile');
                }
              },
              onClose: () => {
                document.body.removeChild(errorModal);
              }
            });
            document.body.appendChild(errorModal);
          } else {
            showToast({
              type: 'error',
              message: error.response?.data?.message || 'Failed to submit expense report'
            });
          }
        }
      },
      onCancel: () => {
        // Dialog will be removed by the component
      }
    };
    if (report.expenditure_request?.title) {
      dialogProps.linkedRequestTitle = report.expenditure_request.title;
    }
    const dialog = createSubmissionDialog(dialogProps);
    document.body.appendChild(dialog);
  }

  /**
   * Handle mark as paid action
   */
  async function handleMarkAsPaid(): Promise<void> {
    try {
      await expenseReportsStore.markAsPaid(reportId);
      showToast({ type: 'success', message: 'Payment marked as complete' });
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to mark as paid',
      });
    }
  }

  /**
   * Handle revert to submitted action
   */
  async function handleRevertToSubmitted(reason: string): Promise<void> {
    try {
      await expenseReportsStore.revertToSubmitted(reportId, reason);
      showToast({ type: 'success', message: 'Status reverted to Submitted' });
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to revert status',
      });
    }
  }

  /**
   * Perform the QuickBooks sync API call, optionally with a manual vendor ID.
   * Returns true if sync succeeded (caller should not restore the button).
   */
  async function performQuickBooksSync(vendorId?: string): Promise<boolean> {
    const qbBtn = document.getElementById('qb-sync-btn') as HTMLButtonElement | null;
    const qbBtnLabel = document.getElementById('qb-sync-btn-label');

    if (qbBtn) {
      qbBtn.disabled = true;
      if (qbBtnLabel) qbBtnLabel.textContent = 'Syncing...';
    }

    try {
      const body: Record<string, string> = {};
      if (vendorId) body.vendorId = vendorId;

      const response = await apiClient.post<{ success: boolean; billId?: string; error?: string }>(`/expense-reports/${reportId}/sync-quickbooks`, body);

      if (response.data?.success) {
        const billId = response.data.billId;
        showToast({
          type: 'success',
          message: `Synced to QuickBooks (Bill #${billId})`,
        });
        await expenseReportsStore.fetchReport(reportId);
        return true;
      } else {
        showToast({
          type: 'error',
          message: response.data?.error || 'QuickBooks sync failed',
        });
        if (qbBtn) qbBtn.disabled = false;
        if (qbBtnLabel) qbBtnLabel.textContent = 'Book in QuickBooks';
        return false;
      }
    } catch (error: unknown) {
      const apiError = error as { code?: string; response?: { code?: string; error?: string; message?: string } };
      const errCode = apiError.code || apiError.response?.code;

      // Vendor lookup failed — prompt user for manual vendor ID
      if (errCode === 'QB_VENDOR_LOOKUP_FAILED') {
        if (qbBtn) qbBtn.disabled = false;
        if (qbBtnLabel) qbBtnLabel.textContent = 'Book in QuickBooks';

        const manualId = await prompt({
          title: 'QuickBooks Vendor Not Found',
          message: 'Automatic vendor matching failed. Please enter the QuickBooks Vendor ID manually. You can find it in QuickBooks under Expenses → Vendors → open the vendor — the ID is the "nameId" parameter in the URL.',
          placeholder: 'e.g. 55',
          confirmText: 'Retry Sync',
          cancelText: 'Cancel',
          validate: (value) => {
            if (!value.trim()) return 'Vendor ID is required';
            if (!/^\d+$/.test(value.trim())) return 'Vendor ID must be a number';
            return null;
          },
        });

        if (manualId) {
          return performQuickBooksSync(manualId.trim());
        }
        return false;
      }

      let message = apiError.response?.error || apiError.response?.message || (error instanceof Error ? error.message : 'QuickBooks sync failed');

      if (errCode === 'QB_NOT_CONNECTED') {
        message = 'QuickBooks is not connected - contact admin';
      } else if (errCode === 'QB_TOKEN_EXPIRED') {
        message = 'QuickBooks connection expired - ask admin to reconnect';
      } else if (errCode === 'QB_ALREADY_SYNCED') {
        message = apiError.response?.error || apiError.response?.message || 'This report was already synced to QuickBooks';
      }

      showToast({ type: 'error', message });

      if (qbBtn) qbBtn.disabled = false;
      if (qbBtnLabel) qbBtnLabel.textContent = 'Book in QuickBooks';
      return false;
    }
  }

  /**
   * Handle sync to QuickBooks action (AC1-AC6)
   */
  async function handleSyncQuickBooks(): Promise<void> {
    await performQuickBooksSync();
  }

  /**
   * Render content based on state
   */
  function render(state: ExpenseReportsState): void {
    const container = document.getElementById('report-content');
    if (!container) return;

    // Loading state
    if (state.loadingDetail) {
      container.innerHTML = '';
      container.appendChild(createLoadingState());
      return;
    }

    // Error state
    if (state.error) {
      container.innerHTML = '';
      container.appendChild(createErrorState(state.error, () => navigateTo('/expense-reports')));
      return;
    }

    // Not found state
    if (!state.selectedReport) {
      container.innerHTML = '';
      container.appendChild(createNotFoundState(() => navigateTo('/expense-reports')));
      return;
    }

    // Render report
    container.innerHTML = '';
    container.appendChild(createReportHeader(state.selectedReport, handleSubmitReport));

    // Show warning banner if linked request is not approved
    const linkedRequest = state.selectedReport.expenditure_request || state.selectedReport.linked_request;
    if (linkedRequest && linkedRequest.status !== 'APPROVED') {
      container.appendChild(createUnapprovedRequestWarning());
    }

    // Show linked request section with budget info
    container.appendChild(createLinkedRequestSection(state.selectedReport));

    // Show payment profile for Team Assistants (AC #4)
    if (isTeamAssistantOrAdmin() && state.selectedReport.payment_profile !== undefined) {
      container.appendChild(createPaymentProfileSection(state.selectedReport));
    }

    // Show action buttons for Team Assistants (AC #1)
    if (isTeamAssistantOrAdmin()) {
      container.appendChild(createActionButtonsSection(
        state.selectedReport,
        handleGenerateEPO,
        handleExportPDF,
        handleSyncQuickBooks
      ));
    }

    // Show payment status actions for Team Assistants and Admins (for non-DRAFT reports)
    if (isTeamAssistantOrAdmin() && state.selectedReport.status !== 'DRAFT') {
      container.appendChild(createPaymentStatusActions({
        status: state.selectedReport.status,
        isLoading: state.loadingDetail,
        onMarkAsPaid: handleMarkAsPaid,
        onRevertToSubmitted: handleRevertToSubmitted,
      }));
    }

    container.appendChild(createReceiptsSection(
      state.selectedReport,
      handleAddReceipt,
      handleReceiptClick,
      handleDeleteReceipt,
      handleDownloadReceipt
    ));

    // Show status history if there are any entries
    if (state.selectedReport.status_history && state.selectedReport.status_history.length > 0) {
      container.appendChild(createStatusHistory(state.selectedReport.status_history));
    }
  }

  // Subscribe to store and fetch report
  setTimeout(() => {
    unsubscribe = expenseReportsStore.subscribe(render);
    expenseReportsStore.fetchReport(reportId);
  }, 0);

  // Cleanup on unmount
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.removedNodes.forEach((node) => {
        if (node === content) {
          if (unsubscribe) {
            unsubscribe();
            unsubscribe = null;
          }
          if (authUnsubscribe) {
            authUnsubscribe();
            authUnsubscribe = null;
          }
          expenseReportsStore.clearSelectedReport();
        }
      });
    });
  });

  setTimeout(() => {
    if (content.parentElement) {
      observer.observe(content.parentElement, { childList: true });
    }
  }, 0);

  // Wrap with sidebar layout
  return createSidebarLayout(content, {
    currentPath: '/expense-reports',
    title: 'Expense Report',
  });
}

export default expenseReportDetailView;
